import SettingsIcon from '@mui/icons-material/Settings';
import { IconButton, Menu, MenuItem, Toolbar, Typography } from '@mui/material';
import { Children, Fragment, isValidElement, ReactNode, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

/** True when `menuItems` will render at least one child in the settings menu. */
export function hasWidgetSettingsMenuContent(menuItems: ReactNode): boolean {
  if (menuItems == null || menuItems === false) return false;
  if (Array.isArray(menuItems)) return menuItems.some(hasWidgetSettingsMenuContent);
  if (isValidElement(menuItems)) {
    if (menuItems.type === Fragment || 'children' in menuItems.props) {
      return Children.toArray(menuItems.props.children).some(hasWidgetSettingsMenuContent);
    }
    // Leaf menu control without child nodes (e.g. a self-closing MenuItem).
    return true;
  }
  // Text nodes and other primitives count as visible menu content.
  return true;
}

export interface WidgetSettingsHeaderProps {
  title: ReactNode;
  onDelete: () => void;
  readOnly?: boolean;
  allowDelete?: boolean;
  /** Rendered before the delete action in the settings menu. */
  menuItems?: ReactNode;
  /** Extra controls shown in the header bar (e.g. console filter/clear). */
  toolbarEnd?: ReactNode;
}

const WidgetSettingsHeader = ({ title, onDelete, readOnly, allowDelete = true, menuItems, toolbarEnd }: WidgetSettingsHeaderProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const hasMenuActions = allowDelete || hasWidgetSettingsMenuContent(menuItems);
  const settingsDisabled = Boolean(readOnly) || !hasMenuActions;

  const handleSettingsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (settingsDisabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleDelete = () => {
    handleClose();
    onDelete();
  };

  return (
    <Toolbar
      className="widget-header"
      variant="dense"
      data-testid="widget-settings-header"
      sx={{
        color: 'text.primary',
        justifyContent: 'center',
        flexShrink: 0,
        pr: 0.5,
        pl: 1.5,
        pt: 0,
        pb: 0,
        minHeight: 40,
        containerType: 'inline-size',
      }}
    >
      <Typography
        component="div"
        sx={{
          flex: 1,
          textAlign: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: '0.75rem',
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>
      {toolbarEnd}
      <IconButton
        color="inherit"
        size="small"
        disabled={settingsDisabled}
        aria-label={t('aria-label.settings-button', 'settings')}
        aria-controls={open ? 'widget-settings-menu' : undefined}
        aria-haspopup={hasMenuActions ? 'true' : undefined}
        aria-expanded={open ? 'true' : undefined}
        onClick={handleSettingsClick}
        sx={{ flexShrink: 0, ml: toolbarEnd ? 0 : 'auto' }}
      >
        <SettingsIcon fontSize="small" />
      </IconButton>
      <Menu
        id="widget-settings-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableRestoreFocus
        slotProps={{
          list: { 'aria-labelledby': 'settings-button', autoFocusItem: false },
        }}
      >
        {menuItems}
        {allowDelete ? (
          <MenuItem onClick={handleDelete}>
            <Trans i18nKey="widgetBase.menuItem.delete-widget">Delete Widget</Trans>
          </MenuItem>
        ) : null}
      </Menu>
    </Toolbar>
  );
};

export default WidgetSettingsHeader;
